import React, { useState, useEffect } from 'react';
import { wallAPI, API_URL } from '../services/api';
import './WallFeed.css';

function WallFeed({ clubId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [ogpPreview, setOgpPreview] = useState(null);
  const [ogpLoading, setOgpLoading] = useState(false);

  const loadMessages = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      const data = await wallAPI.getWallMessages(clubId, pageNum, 10);
      if (append) {
        setMessages(prev => [...prev, ...data.messages]);
      } else {
        setMessages(data.messages);
      }
      setPagination(data.pagination);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to load wall messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [clubId]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || posting) return;

    try {
      setPosting(true);
      const messageText = uploadedImageUrl
        ? `${newMessage.trim()}\n[image:${uploadedImageUrl}]`
        : newMessage.trim();
      const data = await wallAPI.createWallMessage(clubId, messageText);
      setMessages(prev => [data.message, ...prev]);
      setNewMessage('');
      setSelectedImage(null);
      setImagePreview(null);
      setUploadedImageUrl(null);
      setOgpPreview(null);
    } catch (err) {
      setError('Failed to post message');
      console.error(err);
    } finally {
      setPosting(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const data = await wallAPI.uploadImage(file);
      setUploadedImageUrl(data.url);
    } catch (err) {
      setError('Failed to upload image');
      console.error(err);
      setSelectedImage(null);
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
  };

  const detectAndFetchOgp = async (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = text.match(urlRegex);
    if (matches && matches.length > 0) {
      const url = matches[matches.length - 1];
      if (ogpPreview?.url === url) return;
      setOgpLoading(true);
      try {
        const data = await wallAPI.parseOgp(url);
        if (data.title || data.description || data.image) {
          setOgpPreview({ ...data, url });
        }
      } catch {
        setOgpPreview(null);
      } finally {
        setOgpLoading(false);
      }
    } else {
      setOgpPreview(null);
    }
  };

  const handleMessageChange = (e) => {
    const val = e.target.value;
    setNewMessage(val);
    // Debounce OGP detection on space or paste
    if (val.endsWith(' ') || val.endsWith('\n')) {
      detectAndFetchOgp(val);
    }
  };

  const toggleComments = async (messageId) => {
    if (expandedComments[messageId]) {
      setExpandedComments(prev => ({ ...prev, [messageId]: false }));
      return;
    }

    setExpandedComments(prev => ({ ...prev, [messageId]: true }));

    if (!comments[messageId]) {
      try {
        setLoadingComments(prev => ({ ...prev, [messageId]: true }));
        const data = await wallAPI.getComments(messageId);
        setComments(prev => ({ ...prev, [messageId]: data.comments }));
      } catch (err) {
        console.error('Failed to load comments:', err);
      } finally {
        setLoadingComments(prev => ({ ...prev, [messageId]: false }));
      }
    }
  };

  const [submittingComment, setSubmittingComment] = useState({});

  const handleAddComment = async (messageId) => {
    const commentText = newComments[messageId]?.trim();
    if (!commentText || submittingComment[messageId]) return;

    setSubmittingComment(prev => ({ ...prev, [messageId]: true }));

    try {
      const data = await wallAPI.createComment(messageId, commentText);
      setComments(prev => ({
        ...prev,
        [messageId]: [...(prev[messageId] || []), data.comment]
      }));
      setNewComments(prev => ({ ...prev, [messageId]: '' }));
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, commentCount: msg.commentCount + 1 }
          : msg
      ));
      // Auto-expand comments section after adding a comment
      setExpandedComments(prev => ({ ...prev, [messageId]: true }));
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmittingComment(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatBetDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${Math.abs(parseFloat(amount)).toFixed(2)}`;
  };

  const formatOdds = (odds, placeOdds) => {
    if (!odds) return '-';
    if (placeOdds && placeOdds > 0) {
      return `${parseFloat(odds).toFixed(2)} / ${parseFloat(placeOdds).toFixed(2)}`;
    }
    return parseFloat(odds).toFixed(2);
  };

  const generateBetId = (bet) => {
    if (!bet.datePlaced) return '#BET';
    const date = new Date(bet.datePlaced);
    const eventCode = (bet.event || 'BET').substring(0, 3).toUpperCase();
    const raceNum = bet.eventNumber || '1';
    const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = date.getHours().toString().padStart(2, '0') + date.getMinutes().toString().padStart(2, '0');
    return `#${eventCode}${raceNum}-${dateStr}-${timeStr}`;
  };

  const getTypeLabel = (typeId, userName, bet) => {
    switch (typeId) {
      case 1: return 'ANNOUNCEMENT';
      case 2:
        if (bet) {
          const pending = ['pending','accepted','Accepted','unresulted','processing','Alive','Open'].includes(bet.status) || bet.isWin === null || bet.isWin === undefined;
          if (pending) return `${userName?.toUpperCase() || 'USER'} PLACED A BET`;
          const result = bet.isWin ? 'WON' : 'LOST';
          return `${userName?.toUpperCase() || 'USER'} PLACED A BET & ${result}`;
        }
        return `${userName?.toUpperCase() || 'USER'} BET RESULT`;
      case 3: return `${userName?.toUpperCase() || 'USER'} POSTED`;
      case 4: return 'PUNTFACE SAID...';
      default: return userName?.toUpperCase() || 'POST';
    }
  };

  const getTypeIcon = (typeId, bet) => {
    switch (typeId) {
      case 1: return 'fa-bullhorn';
      case 2: {
        const p = ['pending','accepted','Accepted','unresulted','processing','Alive','Open'].includes(bet?.status) || bet?.isWin === null || bet?.isWin === undefined;
        return p ? 'fa-receipt' : (bet?.isWin ? 'fa-trophy' : 'fa-heart-broken');
      }
      case 3: return 'fa-comment';
      case 4: return 'fa-bullhorn';
      default: return 'fa-message';
    }
  };

  const getBetTypeLabel = (type) => {
    switch (type) {
      case 'Leg': return 'Win';
      case 'Multi': return 'Multi';
      case 'Regular': return 'Win';
      case 'EachWay': return 'Each Way';
      default: return type || 'Win';
    }
  };

  const parseSelection = (selection) => {
    if (!selection) return { number: '?', name: selection || '-' };
    const match = selection.match(/^(\d+)\.\s*(.+)$/);
    if (match) {
      return { number: match[1], name: match[2] };
    }
    return { number: '?', name: selection };
  };

  const renderBetSlip = (msg) => {
    const bet = msg.bet;
    if (!bet) {
      return <p className="wall-message-text">{msg.message}</p>;
    }

    const pendingStatuses = ['pending', 'accepted', 'Accepted', 'unresulted', 'processing', 'Alive', 'Open'];
    const isPending = pendingStatuses.includes(bet.status) || bet.isWin === null || bet.isWin === undefined;
    const isWin = !isPending && bet.isWin;
    const returns = isWin ? (bet.payout || 0) : 0;
    const profit = returns - (bet.amount || 0);
    const betId = generateBetId(bet);
    const selection = parseSelection(bet.selection);

    const headerClass = isPending ? 'pending' : (isWin ? '' : 'loss');
    const statusLabel = isPending ? 'PENDING' : (isWin ? 'WON' : 'LOST');
    const titleLabel = isPending ? 'Bet Slip' : (isWin ? 'Winning Bet Slip' : 'Lost Bet Slip');
    const statusIcon = isPending ? 'fa-clock' : (isWin ? 'fa-check-circle' : 'fa-times-circle');
    const titleIcon = isPending ? 'fa-receipt' : (isWin ? 'fa-trophy' : 'fa-times-circle');

    return (
      <div className="bet-slip">
        <div className={`bet-slip-header ${headerClass}`}>
          <span className="bet-slip-title">
            <i className={`fas ${titleIcon}`}></i>
            {titleLabel}
          </span>
          <span className="bet-slip-status">
            <i className={`fas ${statusIcon}`}></i>
            {statusLabel}
          </span>
        </div>
        <div className="bet-slip-body">
          <div className="bet-slip-section">
            <div className="bet-slip-row">
              <span className="bet-slip-label">Bet ID</span>
              <span className="bet-slip-value">{betId}</span>
            </div>
            <div className="bet-slip-row">
              <span className="bet-slip-label">Date & Time</span>
              <span className="bet-slip-value">{formatBetDateTime(bet.datePlaced)}</span>
            </div>
          </div>

          <div className="bet-slip-section">
            <div className="bet-slip-event">
              <div className="bet-slip-event-name">
                <i className="fas fa-horse-head"></i>
                {bet.eventNumber ? `${bet.eventNumber}. ` : ''}{bet.event || 'Event'}
              </div>
              {bet.selection && (
                <div className="bet-slip-selection">
                  <span className="bet-slip-selection-number">{selection.number}</span>
                  <span>{selection.name}</span>
                </div>
              )}
            </div>
            <div className="bet-slip-row">
              <span className="bet-slip-label">Bet Type</span>
              <span className="bet-type-badge">{getBetTypeLabel(bet.type)}</span>
            </div>
            <div className="bet-slip-row">
              <span className="bet-slip-label">Odds</span>
              <span className="odds-badge">{formatOdds(bet.odds, bet.placeOdds)}</span>
            </div>
          </div>

          <div className="bet-slip-section">
            <div className="bet-slip-row">
              <span className="bet-slip-label">Stake</span>
              <span className="bet-slip-value large">{formatCurrency(bet.amount)}</span>
            </div>
          </div>
        </div>
        <div className="bet-slip-footer">
          <div className="bet-slip-totals">
            <div className="bet-slip-total-item">
              <div className="bet-slip-total-label">Staked</div>
              <div className="bet-slip-total-value stake">{formatCurrency(bet.amount)}</div>
            </div>
            {isPending ? (
              <div className="bet-slip-total-item">
                <div className="bet-slip-total-label">Potential Return</div>
                <div className="bet-slip-total-value">{formatCurrency((bet.amount || 0) * (bet.odds || 0))}</div>
              </div>
            ) : (
              <>
                <div className="bet-slip-total-item">
                  <div className="bet-slip-total-label">Returns</div>
                  <div className={`bet-slip-total-value ${isWin ? 'win' : ''}`}>{formatCurrency(returns)}</div>
                </div>
                <div className="bet-slip-total-item">
                  <div className="bet-slip-total-label">{isWin ? 'Profit' : 'Loss'}</div>
                  <div className={`bet-slip-total-value ${isWin ? 'win' : ''}`}>
                    {profit >= 0 ? '+' : '-'}{formatCurrency(Math.abs(profit))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="bet-slip-barcode" data-bet-id={betId.replace('#', '')}></div>
        </div>
      </div>
    );
  };

  if (loading && messages.length === 0) {
    return <div className="wall-feed-loading">Loading wall...</div>;
  }

  return (
    <div className="wall-feed">
      {/* Post Box */}
      <div className="wall-post-box">
        <form onSubmit={handlePost}>
          <div className="wall-post-input-wrapper">
            <span className="voice-icon"><i className="fas fa-microphone"></i></span>
            <textarea
              className="wall-post-input"
              value={newMessage}
              onChange={handleMessageChange}
              onPaste={() => setTimeout(() => detectAndFetchOgp(newMessage), 200)}
              placeholder="What's on your mind?"
              maxLength="5000"
            />
          </div>
          {imagePreview && (
            <div style={{ padding: '0 12px 8px', position: 'relative', display: 'inline-block' }}>
              <img src={imagePreview} alt="Preview" style={{ maxHeight: 120, borderRadius: 8 }} />
              {uploadingImage && <span style={{ marginLeft: 8, color: '#fbbf24', fontSize: 12 }}>Uploading...</span>}
              <button type="button" onClick={handleRemoveImage} style={{
                position: 'absolute', top: 2, right: 14, background: 'rgba(0,0,0,0.7)', color: '#fff',
                border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12, lineHeight: '22px', padding: 0,
              }}>&times;</button>
            </div>
          )}
          {ogpLoading && <div style={{ padding: '0 12px 8px', color: '#999', fontSize: 12 }}>Loading link preview...</div>}
          {ogpPreview && !ogpLoading && (
            <div style={{
              margin: '0 12px 8px', padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              {ogpPreview.image && (
                <img src={ogpPreview.image} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
              )}
              <div style={{ minWidth: 0 }}>
                {ogpPreview.title && <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{ogpPreview.title}</div>}
                {ogpPreview.description && <div style={{ color: '#999', fontSize: 11, lineHeight: 1.3 }}>{ogpPreview.description.substring(0, 120)}{ogpPreview.description.length > 120 ? '...' : ''}</div>}
                <div style={{ color: '#666', fontSize: 10, marginTop: 2 }}>{ogpPreview.url}</div>
              </div>
              <button type="button" onClick={() => setOgpPreview(null)} style={{
                background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0,
              }}>&times;</button>
            </div>
          )}
          <div className="wall-post-actions">
            <div className="wall-post-tools">
              <button type="button" className="wall-tool-btn" title="GIF">GIF</button>
              <label className="wall-tool-btn" title="Image" style={{ cursor: 'pointer' }}>
                <i className="fas fa-image"></i>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
              </label>
              <button type="button" className="wall-tool-btn" title="Video"><i className="fas fa-video"></i></button>
            </div>
            <button type="submit" className="wall-submit-btn" disabled={posting || !newMessage.trim()}>
              {posting ? 'Posting...' : 'SUBMIT'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="wall-error">{error}</div>}

      {/* Activity Feed */}
      {messages.map((msg) => (
        <article key={msg.id} className="wall-activity-card">
          <div className="wall-activity-header">
            <div className="wall-activity-avatar">
              {msg.userImage ? (
                <img src={`https://puntclub-media.s3.amazonaws.com/${msg.userImage}`} alt=""
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = ''); }}
                />
              ) : null}
              <i className={`fas ${msg.userId ? 'fa-user' : getTypeIcon(msg.typeId, msg.bet)}`} style={msg.userImage ? { display: 'none' } : {}}></i>
            </div>
            <div className="wall-activity-info">
              <div className="wall-activity-user">
                <i className={`fas ${getTypeIcon(msg.typeId, msg.bet)}`}></i>
                <span>{getTypeLabel(msg.typeId, msg.userName, msg.bet)}</span>
              </div>
              {msg.typeId === 4 && (
                <div className="wall-activity-message">Here's how your club went yesterday.</div>
              )}
            </div>
            <div className="wall-activity-time">{formatDate(msg.displayTimestamp)}</div>
          </div>

          <div className="wall-activity-content">
            {msg.typeId === 2 ? renderBetSlip(msg) : (
              <p className="wall-message-text">{msg.message}</p>
            )}
          </div>

          {/* Comment Section */}
          <div className="wall-comment-section">
            {msg.commentCount > 0 && (
              <button
                className="wall-view-comments-btn"
                onClick={() => toggleComments(msg.id)}
              >
                {expandedComments[msg.id] ? 'Hide' : 'View'} {msg.commentCount} comment{msg.commentCount !== 1 ? 's' : ''}
              </button>
            )}

            {expandedComments[msg.id] && (
              <div className="wall-comments-list">
                {loadingComments[msg.id] ? (
                  <div className="wall-comments-loading">Loading comments...</div>
                ) : (
                  comments[msg.id]?.map((comment) => (
                    <div key={comment.id} className="wall-comment">
                      <div className="wall-comment-avatar">
                        {comment.userImage ? (
                          <img src={`https://puntclub-media.s3.amazonaws.com/${comment.userImage}`} alt=""
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = ''); }}
                          />
                        ) : null}
                        <i className="fas fa-user" style={comment.userImage ? { display: 'none' } : {}}></i>
                      </div>
                      <div className="wall-comment-content">
                        <div className="wall-comment-header">
                          <span className="wall-comment-author">{comment.userName}</span>
                          <span className="wall-comment-time">{formatDate(comment.created)}</span>
                        </div>
                        <p className="wall-comment-text">{comment.comment}</p>
                        {comment.attachmentUrl && comment.attachmentType === 'image' && (
                          <img src={`http://localhost:8001${comment.attachmentUrl}`} alt=""
                            style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, marginTop: 6 }} />
                        )}
                        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                          <button onClick={() => setNewComments(prev => ({ ...prev, [msg.id]: `@${comment.userName} ` }))}
                            style={{ background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                            Reply
                          </button>
                          {['👍', '😂', '🔥'].map(emoji => (
                            <button key={emoji} onClick={() => {/* TODO: reaction API */}}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0, opacity: 0.5 }}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="wall-comment-input">
              <div className="wall-comment-input-avatar"></div>
              <input
                type="text"
                placeholder="Add a comment"
                value={newComments[msg.id] || ''}
                onChange={(e) => setNewComments(prev => ({
                  ...prev,
                  [msg.id]: e.target.value
                }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddComment(msg.id);
                  }
                }}
              />
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <i className="fas fa-image" style={{ color: '#666', fontSize: 16 }}></i>
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('image', file);
                    formData.append('comment', newComments[msg.id] || '');
                    try {
                      const res = await wallAPI.createCommentWithImage(msg.id, formData);
                      if (res.comment) {
                        setComments(prev => ({ ...prev, [msg.id]: [...(prev[msg.id] || []), res.comment] }));
                        setNewComments(prev => ({ ...prev, [msg.id]: '' }));
                        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, commentCount: m.commentCount + 1 } : m));
                        setExpandedComments(prev => ({ ...prev, [msg.id]: true }));
                      }
                    } catch (err) { console.error('Image upload failed:', err); }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>
        </article>
      ))}

      {/* Load More */}
      {pagination && page < pagination.pages && (
        <button
          className="wall-load-more"
          onClick={() => loadMessages(page + 1, true)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Load More'}
        </button>
      )}

      {messages.length === 0 && !loading && (
        <div className="wall-empty">
          <i className="fas fa-comments"></i>
          <p>No wall posts yet. Be the first to post!</p>
        </div>
      )}
    </div>
  );
}

export default WallFeed;
